import { useCallback, useEffect, useState } from "react";
import { Building2, Loader2, Plus, Trash2 } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { CorporateDetail } from "@/components/CorporateDetail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const EMPTY = {
  name: "", billing_email: "", billing_contact: "", billing_address: "", phone: "",
  terms: "net_30", credit_limit: 0, discount_percent: 0, po_required: false, notes: "",
  active: true,
};

const Field = ({ label, testId, hint, ...props }) => (
  <label className="block">
    <span className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
    <Input data-testid={testId} className="h-11 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" {...props} />
    {hint && <span className="mt-1 block text-[11px] text-slate-500">{hint}</span>}
  </label>
);

export default function Corporate() {
  const [accounts, setAccounts] = useState([]);
  const [terms, setTerms] = useState({});
  const [available, setAvailable] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => api.get("/corporate-accounts").then(({ data }) => {
    setAccounts(data.accounts);
    setTerms(data.terms);
    setAvailable(data.available);
  }).catch((e) => setError(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const set = (key) => (e) => setForm((f) => ({
    ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
  }));

  const save = async () => {
    setBusy(true); setError("");
    const payload = {
      ...form,
      credit_limit: Number(form.credit_limit) || 0,
      discount_percent: Number(form.discount_percent) || 0,
    };
    try {
      if (form.id) await api.put(`/corporate-accounts/${form.id}`, payload);
      else await api.post("/corporate-accounts", payload);
      setForm(null);
      load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async (account) => {
    setError("");
    try {
      await api.delete(`/corporate-accounts/${account.id}`);
      if (selected === account.id) setSelected(null);
      load();
    } catch (e) { setError(errMsg(e)); }
  };

  return (
    <div data-testid="corporate-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-50">Corporate accounts</h1>
          <p className="mt-2 text-sm text-slate-400">
            Bill a company monthly instead of charging a card per ride. Their authorised travellers
            book without paying, and every trip lands on one statement.
          </p>
        </div>
        <Button data-testid="corporate-new" onClick={() => setForm({ ...EMPTY })}
          className="h-11 bg-gold text-ink-900 hover:bg-gold-light">
          <Plus className="mr-2 h-4 w-4" /> New account
        </Button>
      </div>

      {!available && (
        <p data-testid="corporate-upgrade" className="rounded-xl border border-gold/30 bg-gold/5 p-4 text-sm text-slate-300">
          Corporate accounts are part of the Professional plan and above. Upgrade under Plans &
          Billing and you can start billing companies on Net-30 terms.
        </p>
      )}
      {error && <p data-testid="corporate-error" className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300">{error}</p>}

      {form && (
        <div data-testid="corporate-form" className="rounded-xl border border-ink-600 bg-ink-800 p-5 sm:p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">
            {form.id ? `Edit ${form.name}` : "New corporate account"}
          </p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Company name" testId="corporate-name" value={form.name} onChange={set("name")} placeholder="ABC Corporation" />
            <Field label="Accounts payable email" testId="corporate-billing-email" value={form.billing_email} onChange={set("billing_email")} placeholder="ap@abccorp.com" />
            <Field label="Billing contact" testId="corporate-contact" value={form.billing_contact} onChange={set("billing_contact")} placeholder="Jane Doe" />
            <Field label="Phone" testId="corporate-phone" value={form.phone} onChange={set("phone")} placeholder="713-555-0000" />
            <Field label="Billing address" testId="corporate-address" value={form.billing_address} onChange={set("billing_address")} placeholder="1200 Smith St, Houston, TX" />
            <label className="block">
              <span className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Payment terms</span>
              <select data-testid="corporate-terms" value={form.terms} onChange={set("terms")}
                className="h-11 w-full rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold">
                {Object.entries(terms).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
            </label>
            <Field label="Credit limit" testId="corporate-credit-limit" type="number" min="0" step="100"
              value={form.credit_limit} onChange={set("credit_limit")}
              hint="Blocks new corporate bookings once unpaid trips pass this. 0 = no limit." />
            <Field label="Negotiated discount %" testId="corporate-discount" type="number" min="0" max="50" step="1"
              value={form.discount_percent} onChange={set("discount_percent")}
              hint="Comes off every trip billed to this account." />
          </div>
          <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm text-slate-300">
            <input data-testid="corporate-po" type="checkbox" checked={form.po_required} onChange={set("po_required")}
              className="h-4 w-4 accent-[#D4AF37]" />
            Their travellers must give a PO / cost centre when booking
          </label>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button data-testid="corporate-save" disabled={busy || !form.name || !form.billing_email}
              onClick={save} className="h-11 bg-gold text-ink-900 hover:bg-gold-light">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save account"}
            </Button>
            <Button data-testid="corporate-cancel" variant="outline" onClick={() => setForm(null)}
              className="h-11 border-ink-600 bg-transparent text-slate-300 hover:text-gold">Cancel</Button>
          </div>
        </div>
      )}

      {accounts.length === 0 ? (
        <p data-testid="corporate-empty" className="rounded-xl border border-ink-600 bg-ink-800 p-10 text-center text-sm text-slate-500">
          No corporate accounts yet. Add the company, list who is allowed to book, and their rides
          go on one monthly bill.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {accounts.map((a) => (
            <div key={a.id} data-testid={`corporate-card-${a.id}`}
              className={`rounded-xl border bg-ink-800 p-5 transition-colors ${
                selected === a.id ? "border-gold/50" : "border-ink-600 hover:border-gold/30"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 truncate font-display text-lg font-semibold text-slate-50">
                    <Building2 className="h-4 w-4 shrink-0 text-gold" /> {a.name}
                  </p>
                  <p className="mt-1 truncate text-xs text-slate-500">{a.billing_email}</p>
                </div>
                <span className="shrink-0 rounded-full border border-ink-600 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                  {a.terms_label}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Unpaid</p>
                  <p data-testid={`corporate-outstanding-${a.id}`}
                    className={`text-lg font-bold ${a.outstanding > 0 ? "text-amber-300" : "text-slate-300"}`}>
                    {money(a.outstanding)}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">This month</p>
                  <p className="text-lg font-bold text-slate-100">{money(a.spend_this_month)}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Travellers</p>
                  <p className="text-lg font-bold text-slate-100">{(a.travellers || []).length}</p>
                </div>
              </div>
              {a.credit_limit > 0 && (
                <p className="mt-3 text-[11px] text-slate-500">
                  Credit limit {money(a.credit_limit)}
                  {a.discount_percent > 0 ? ` · ${a.discount_percent}% off every trip` : ""}
                </p>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <Button data-testid={`corporate-open-${a.id}`} variant="outline"
                  onClick={() => setSelected(selected === a.id ? null : a.id)}
                  className="h-10 border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                  {selected === a.id ? "Close" : "Travellers & statements"}
                </Button>
                <Button data-testid={`corporate-edit-${a.id}`} variant="outline"
                  onClick={() => setForm({ ...EMPTY, ...a })}
                  className="h-10 border-ink-600 bg-transparent text-slate-300 hover:text-gold">Edit</Button>
                <button data-testid={`corporate-delete-${a.id}`} onClick={() => remove(a)}
                  className="ml-auto text-slate-500 transition-colors hover:text-red-400" title="Remove account">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && <CorporateDetail accountId={selected} onChange={load} />}
    </div>
  );
}
